/**
 * Mantine select-option helpers.
 *
 * Mantine's Combobox-based inputs (`Select`, `MultiSelect`, `Autocomplete`,
 * `TagsInput`) **throw** when two options share a `value`:
 *
 *   `[@mantine/core] Duplicate options are not supported. Option with value
 *    "Birthday" was provided more than once`
 *
 * The throw happens during render, so React unwinds the entire tree and the
 * page goes blank. Any option list whose `value` is a human-entered name
 * (event type, card attribute, ...) is exposed to this: names are not unique
 * in the database, so an admin — or leftover test data — creating a second
 * "Birthday" is enough to white-screen the public landing page. This was
 * observed on the CI gate, where the shared test catalog held two
 * system-defined "Birthday" event types.
 *
 * Deduping at the boundary keeps a data condition from becoming an outage.
 */

/** Minimal shape shared by every Mantine option object. */
interface ValuedOption {
  value: string;
}

/**
 * Returns the options with duplicate `value` entries removed, keeping the
 * first occurrence so caller-defined ordering is preserved.
 *
 * Comparison is case-insensitive because SQL Server collations are
 * case-insensitive by default: "Birthday" and "birthday" reach the client as
 * two options but resolve to the same record on submit.
 *
 * @param options Option objects in display order.
 */
export function dedupeOptionsByValue<T extends ValuedOption>(options: T[]): T[] {
  const seen = new Set<string>();

  return options.filter((option) => {
    const key = option.value.toLowerCase();
    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
}

/** Minimal option shape for {@link withDeprecated}. */
interface LabeledOption {
  value: string;
  label: string;
}

/**
 * Returns `options` plus the record's current value when that value was retired from the active list,
 * labelled "(Deprecated)", so an existing contact or template still shows (and keeps) its saved value
 * while users are nudged to pick a current one.
 *
 * @param options Active options in display order.
 * @param currentValue The value currently stored on the record being edited.
 */
export function withDeprecated<T extends LabeledOption>(
  options: T[],
  currentValue: string | null | undefined,
): LabeledOption[] {
  if (!currentValue) return options;
  if (options.some((o) => o.value === currentValue)) return options;
  return [...options, { value: currentValue, label: `${currentValue} (Deprecated)` }];
}
